import { useMemo } from 'react';
import type { Exercise, EffortScale, WeeklyTargetRow } from '@heraion/shared';
import { computeInWorkoutTargets, MINUTE_MS } from '@heraion/shared';

import type { LoggerDraft } from './state/loggerDraftTypes';
import { useSessionResults } from '../index';
import { useNowTick } from '../useNowTick';
import { useInWorkoutMeterEnabled } from './state/inWorkoutMeterPreference';
import { buildPayload } from './workoutMapping';
import type { LoggerHostDeps } from './hostDeps';

// AP-3b — the in-workout contextual meter's data source. Scores the muscles the
// ACTIVE exercise trains against the athlete's weekly targets, with THIS
// session's just-logged sets already folded in (computeInWorkoutTargets does the
// fold via the shared computeSessionResult → computeWeeklyTargets path). Mirrors
// PlanScreen's Home wiring: session results (cache-first SWR) + the athlete's
// targets; adds only the live-draft fold on top. Returns [] until
// the resources land or when nothing is contextual — the strip hides itself.
//
// RW-18: `sessionResultsDeps` is injected (both hosts already build one —
// mobile's apps/mobile/src/progress/progressDeps.ts, web-rnw's
// apps/web-rnw/src/progress/progressDeps.ts — useSessionResults itself was
// already portable/shared). `profile` and `athleteTargets` are
// host-resolved DATA rather than called hooks:
// mobile's copies are its own app-wide singletons/caches (and, for
// athleteTargets specifically, a DELIBERATELY un-repointed duplicate of
// the shared hook — see hostDeps.ts's doc — so this move must not
// silently fix that debt as a side effect).
export function useInWorkoutTargets(
  draft: LoggerDraft,
  activeExercise: Exercise | null,
  catalog: Exercise[] | null,
  effortScale: EffortScale,
  deps: LoggerHostDeps,
): WeeklyTargetRow[] {
  const { enabled } = useInWorkoutMeterEnabled();
  const { results: sessionResults } = useSessionResults(deps.sessionResultsDeps);
  const { profile, athleteTargets } = deps;

  const bodyweightKg = profile.bodyweightKg ?? 0;
  // The last 7 days slide: re-read the clock each minute.
  const nowMs = useNowTick(true, MINUTE_MS);

  // A signature of only the COMPLETED sets — the sole draft state the fold reads
  // (buildPayload drops uncompleted sets before we ever see them). Typing in the
  // still-pending "next set" input leaves this unchanged, so the expensive fold
  // below doesn't rerun on every keystroke.
  const completedSig = useMemo(
    () =>
      draft.entries
        .map(
          (e) =>
            e.exerciseId +
            ':' +
            e.sets
              .filter((s) => s.completedAt !== null)
              .map(
                (s) =>
                  `${s.reps}x${s.weight ?? ''}r${s.rir ?? ''}f${s.fail ? 1 : 0}d${s.duration ?? ''}`,
              )
              .join(','),
        )
        .join('|'),
    [draft],
  );

  return useMemo(
    () => {
      if (!enabled || !activeExercise || catalog === null || sessionResults === null) {
        return [];
      }
      const built = buildPayload(draft, effortScale, { dropUncompleted: true });
      const draftWorkout = built.ok ? built.payload : null;
      return computeInWorkoutTargets(
        sessionResults,
        draftWorkout,
        catalog,
        activeExercise,
        bodyweightKg,
        nowMs,
        athleteTargets,
      );
    },
    // `draft` is intentionally omitted: completedSig captures the completed-set
    // state the fold depends on, so keying on the whole draft (a fresh object
    // each keystroke) would recompute the fold pointlessly on pending edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      enabled,
      completedSig,
      sessionResults,
      catalog,
      activeExercise,
      bodyweightKg,
      effortScale,
      nowMs,
      athleteTargets,
    ],
  );
}
